import {
	Box,
	Callout,
	Heading,
	IconSolidChartBar,
	Stack,
	Text,
} from '@highlight-run/ui/components'
import { useParams } from '@util/react-router/useParams'
import moment from 'moment'
import React, { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useProjectAnalyticsReferrerPagesQuery } from '@/graph/generated/hooks'

const numberFormatter = new Intl.NumberFormat()

const ReferrerAnalyticsPage = () => {
	const { project_id } = useParams<{ project_id: string }>()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const source = searchParams.get('source') ?? '(none)'

	const range = useMemo(
		() => ({
			startAt: moment().subtract(30, 'days').startOf('day').toDate(),
			endAt: moment().endOf('day').toDate(),
		}),
		[],
	)

	const { data, loading, error } = useProjectAnalyticsReferrerPagesQuery({
		variables: {
			project_id: project_id ?? '',
			start_at: range.startAt.toISOString(),
			end_at: range.endAt.toISOString(),
			referrer: source,
		},
		skip: !project_id,
		fetchPolicy: 'cache-and-network',
	})

	const pages = data?.project_analytics_referrer_pages ?? []
	const totalVisits = pages.reduce((sum, page) => sum + page.value, 0)

	return (
		<Box
			display="flex"
			flexDirection="column"
			height="full"
			overflowY="auto"
			background="default"
			p="32"
			gap="24"
		>
			<Stack direction="column" gap="6">
				<Box
					as="button"
					p="0"
					onClick={() => navigate('../analytics')}
					style={{
						cursor: 'pointer',
						textAlign: 'left',
						background: 'none',
						border: 'none',
					}}
				>
					<Text color="secondaryContentText">← Back to Analytics</Text>
				</Box>
				<Heading level="h3">Traffic from {source}</Heading>
				<Text color="moderate">
					Landing pages and visit counts from this referrer over the
					last 30 days.
				</Text>
			</Stack>

			{error ? (
				<Callout icon={IconSolidChartBar} title="Analytics unavailable">
					<Text color="moderate">
						Could not load referrer page breakdown.
					</Text>
				</Callout>
			) : null}

			<Box
				gap="12"
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
				}}
			>
				<Stat label="Total visits" value={loading ? undefined : totalVisits} />
				<Stat label="Pages" value={loading ? undefined : pages.length} />
			</Box>

			<Box border="secondary" borderRadius="8" background="raised" p="16">
				<Stack direction="column" gap="12">
					<Text weight="bold">Pages</Text>
					<Stack direction="column" gap="8">
						{loading ? (
							<Text color="moderate">Loading...</Text>
						) : pages.length ? (
							pages.map((page) => (
								<Box
									key={page.name}
									display="flex"
									alignItems="center"
									gap="12"
								>
									<Text lines="1">{page.name}</Text>
									<Box ml="auto">
										<Text color="moderate">
											{numberFormatter.format(page.value)}
										</Text>
									</Box>
								</Box>
							))
						) : (
							<Text color="moderate">No page data for this referrer</Text>
						)}
					</Stack>
				</Stack>
			</Box>
		</Box>
	)
}

const Stat = ({
	label,
	value,
}: {
	label: string
	value: number | string | undefined
}) => (
	<Box border="secondary" borderRadius="8" background="raised" p="16">
		<Stack direction="column" gap="4">
			<Text color="moderate">{label}</Text>
			<Text size="large" weight="bold">
				{typeof value === 'number'
					? numberFormatter.format(value)
					: value ?? '-'}
			</Text>
		</Stack>
	</Box>
)

export default ReferrerAnalyticsPage
